import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import Layout from '@/components/Layout';
import { RegionContextLine } from '@/components/jobs/RegionContextLine';
import { OpportunityActions } from '@/components/jobs/OpportunityActions';
import { JobCommandCenter } from '@/components/employer/JobCommandCenter';
import { skillbridgeDisplay, skillbridgeSections } from '@/lib/skillbridge';
import { MapPin, Briefcase, DollarSign, Building2, Edit2, Power, Trash2, AlertTriangle, Loader2, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function JobDetails() {
  const { jobId } = useParams();
  const [user, setUser] = useState(null);
  const [job, setJob] = useState(null);
  const [employer, setEmployer] = useState(null);
  const [resume, setResume] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // Employer management state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [editForm, setEditForm] = useState({
    title: '',
    description: '',
    remote: false,
    job_type: 'full_time',
    salary_min: '',
    salary_max: '',
    requirements: '',
    employer_location_id: ''
  });
  const [savedLocations, setSavedLocations] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const jobRes = await axios.get(`${BACKEND_URL}/api/jobs/${jobId}`);
        setJob(jobRes.data);
        
        try {
          const userRes = await axios.get(`${BACKEND_URL}/api/auth/me`, { withCredentials: true });
          setUser(userRes.data);
          
          // If user is authenticated, try to get employer profile and resume
          const [employerRes, resumeRes] = await Promise.allSettled([
            axios.get(`${BACKEND_URL}/api/employer/profile`, { withCredentials: true }),
            axios.get(`${BACKEND_URL}/api/resume`, { withCredentials: true })
          ]);
          
          if (employerRes.status === 'fulfilled' && employerRes.value.data) {
            setEmployer(employerRes.value.data);
          }
          if (resumeRes.status === 'fulfilled' && resumeRes.value.data) {
            setResume(resumeRes.value.data);
          }
        } catch (err) {
          // Not logged in - that's fine
        }
      } catch (error) {
        toast.error('Job not found');
        navigate('/jobs');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [jobId, navigate]);

  // Initialize edit form when opening dialog
  useEffect(() => {
    if (editDialogOpen && job) {
      setEditForm({
        title: job.title || '',
        description: job.description || '',
        remote: job.remote || false,
        job_type: job.job_type || 'full_time',
        salary_min: job.salary_min?.toString() || '',
        salary_max: job.salary_max?.toString() || '',
        requirements: Array.isArray(job.requirements) ? job.requirements.join('\n') : (job.requirements || ''),
        employer_location_id: job.employer_location_id || ''
      });
      // Fetch saved locations for the employer
      const ownerMatch = user?.role === 'employer' && employer && job?.employer_id === employer.employer_id;
      if (ownerMatch) {
        axios.get(`${BACKEND_URL}/api/employer/locations`, { withCredentials: true })
          .then(res => {
            const active = (res.data.locations || []).filter(l => l.is_active !== false);
            setSavedLocations(active);
          })
          .catch(() => setSavedLocations([]));
      }
    }
  }, [editDialogOpen, job, user, employer]);

  // Computed authorization flags
  const isEmployer = user?.role === 'employer';
  const isOwnerEmployer = isEmployer && employer && job?.employer_id === employer.employer_id;
  const isAdmin = user?.role === 'admin';
  const canManageJob = isOwnerEmployer || isAdmin;
  const isJobActive = job?.status === 'active';

  // Employer management handlers
  const handleEditJob = async () => {
    setActionLoading(true);
    try {
      const payload = {
        title: editForm.title,
        description: editForm.description,
        remote: editForm.remote,
        job_type: editForm.job_type,
        salary_min: editForm.salary_min ? parseInt(editForm.salary_min) : null,
        salary_max: editForm.salary_max ? parseInt(editForm.salary_max) : null,
        requirements: editForm.requirements
          ? (typeof editForm.requirements === 'string'
              ? editForm.requirements.split('\n').map(r => r.trim()).filter(Boolean)
              : editForm.requirements)
          : null,
      };
      if (editForm.employer_location_id) {
        payload.employer_location_id = editForm.employer_location_id;
      }
      
      await axios.put(`${BACKEND_URL}/api/jobs/${jobId}`, payload, { withCredentials: true });
      
      const jobRes = await axios.get(`${BACKEND_URL}/api/jobs/${jobId}`);
      setJob(jobRes.data);
      
      toast.success('Job updated successfully');
      setEditDialogOpen(false);
    } catch (error) {
      const detail = error.response?.data?.detail;
      let msg = 'Failed to update job';
      if (typeof detail === 'string') {
        msg = detail;
      } else if (Array.isArray(detail)) {
        msg = detail.map(d => typeof d === 'object' ? (d.msg || JSON.stringify(d)) : String(d)).join('; ');
      } else if (detail && typeof detail === 'object') {
        msg = detail.msg || JSON.stringify(detail);
      }
      toast.error(msg);
    } finally {
      setActionLoading(false);
    }
  };

  const handleToggleStatus = async () => {
    setActionLoading(true);
    const newStatus = isJobActive ? 'inactive' : 'active';
    try {
      await axios.put(
        `${BACKEND_URL}/api/jobs/${jobId}`,
        { status: newStatus },
        { withCredentials: true }
      );
      
      // Refresh job data
      const jobRes = await axios.get(`${BACKEND_URL}/api/jobs/${jobId}`);
      setJob(jobRes.data);
      
      toast.success(`Job ${newStatus === 'active' ? 'activated' : 'deactivated'} successfully`);
      setStatusDialogOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update job status');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteJob = async () => {
    setActionLoading(true);
    try {
      await axios.delete(`${BACKEND_URL}/api/jobs/${jobId}`, { withCredentials: true });
      toast.success('Job deleted successfully');
      navigate('/employer/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete job');
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <Layout user={user}>
        <div className="flex justify-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand"></div>
        </div>
      </Layout>
    );
  }

  if (!job) return null;

  const isSkillbridge = job.opportunity_type === 'skillbridge';
  const sb = isSkillbridge ? skillbridgeDisplay(job) : null;
  const sbSections = isSkillbridge ? skillbridgeSections(job) : null;
  // Employer command-center mode: owner (or admin) viewing their own NATIVE job.
  const commandCenter = canManageJob && job.opportunity_type !== 'skillbridge' && job.source_type !== 'external';
  const daysOpen = job.posted_at ? Math.max(0, Math.floor((Date.now() - new Date(job.posted_at).getTime()) / 86400000)) : null;

  return (
    <Layout user={user}>
      <div className={`${commandCenter ? 'max-w-6xl' : 'max-w-4xl'} mx-auto px-4 sm:px-6 lg:px-8 py-8`}>
        <Button
          onClick={() => navigate(isEmployer ? '/employer/dashboard' : '/jobs')}
          variant="outline"
          className="mb-6 border-slate-300 text-slate-700"
          data-testid="job-detail-back-btn"
        >
          ← {isEmployer ? 'Back to Jobs' : 'Back to Jobs'}
        </Button>

        {commandCenter && (
          <div className="mb-8" data-testid="employer-command-center">
            {/* Hiring identity + quick actions header */}
            <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <h1 className="text-3xl font-bold text-slate-900" data-testid="cc-job-title">{job.title}</h1>
                  <span className={`text-[11px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full ${isJobActive ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-amber-50 text-amber-700 border border-amber-200'}`}>{job.status}</span>
                </div>
                <div className="flex items-center gap-x-4 gap-y-1 text-sm text-slate-500 flex-wrap">
                  <span className="inline-flex items-center gap-1"><MapPin className="w-4 h-4" />{job.location}</span>
                  {job.job_type && <span className="inline-flex items-center gap-1"><Briefcase className="w-4 h-4" />{job.job_type.replace('_', ' ').toUpperCase()}</span>}
                  {daysOpen !== null && <span>Open {daysOpen} day{daysOpen === 1 ? '' : 's'}</span>}
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button data-testid="cc-edit-job-btn" onClick={() => setEditDialogOpen(true)} variant="outline" size="sm" className="border-slate-300 text-slate-700"><Edit2 className="w-4 h-4 mr-1.5" /> Edit</Button>
                <Button data-testid="cc-toggle-status-btn" onClick={() => setStatusDialogOpen(true)} variant="outline" size="sm" className={isJobActive ? 'border-amber-300 text-amber-700' : 'border-green-300 text-green-700'}><Power className="w-4 h-4 mr-1.5" />{isJobActive ? 'Pause' : 'Activate'}</Button>
              </div>
            </div>

            <JobCommandCenter jobId={jobId} jobTitle={job.title} />

            {/* Job description moved BELOW hiring controls */}
            <Card className="p-6 rounded-2xl border border-slate-200 mt-6" data-testid="cc-job-description">
              <h2 className="text-base font-bold text-slate-900 mb-3">Your posting</h2>
              <p className="text-slate-700 whitespace-pre-wrap text-[15px] leading-relaxed">{job.description}</p>
              {job.requirements && (Array.isArray(job.requirements) ? job.requirements.length > 0 : !!job.requirements) && (
                <div className="mt-5">
                  <h3 className="text-sm font-semibold text-slate-900 mb-2">Requirements</h3>
                  <p className="text-slate-700 whitespace-pre-wrap text-[15px] leading-relaxed">{Array.isArray(job.requirements) ? job.requirements.join('\n') : job.requirements}</p>
                </div>
              )}
            </Card>
          </div>
        )}

        {!commandCenter && (
        <Card className="p-8 border border-slate-200 shadow-sm" data-testid="job-details-card">
          {/* Job Status Badge for owners */}
          {canManageJob && job.status !== 'active' && (
            <div className="mb-4 px-3 py-2 bg-amber-50 border border-amber-200 rounded-md flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-600" />
              <span className="text-sm text-amber-700 font-medium">
                This job is currently {job.status}. Only you can see it.
              </span>
            </div>
          )}

          <div className="mb-6">
            <h1 className="text-3xl font-bold text-slate-900 mb-4" data-testid="job-detail-title">{isSkillbridge ? sb.displayTitle : job.title}</h1>

            {isSkillbridge && sb.showProvider && (
              <div className="flex items-center gap-2 text-slate-700 font-medium mb-3" data-testid="job-detail-provider">
                <Building2 className="w-5 h-5 text-slate-400" />
                <span><span className="text-slate-400 font-normal">Provider: </span>{sb.provider}</span>
              </div>
            )}

            <div className="flex flex-wrap gap-4 text-slate-600 mb-6">
              <div className="flex items-center gap-2">
                <MapPin className="w-5 h-5" />
                {job.location}
              </div>
              {job.remote && (
                <span className="px-3 py-1 bg-green-100 text-green-800 rounded-md text-sm font-medium">Remote</span>
              )}
              {job.opportunity_type === 'skillbridge' && (
                <span className="px-3 py-1 bg-amber-100 text-amber-800 rounded-md text-sm font-medium" data-testid="job-detail-skillbridge-badge">SkillBridge</span>
              )}
              {isSkillbridge && sb.showProgramType && (
                <div className="flex items-center gap-2" data-testid="job-detail-program-type">
                  <Briefcase className="w-5 h-5" />
                  {sb.programType}
                </div>
              )}
              {!isSkillbridge && job.job_type && (
                <div className="flex items-center gap-2">
                  <Briefcase className="w-5 h-5" />
                  {job.job_type.replace('_', ' ').toUpperCase()}
                </div>
              )}
              {(job.salary_min || job.salary_max) && (
                <div className="flex items-center gap-2">
                  <DollarSign className="w-5 h-5" />
                  {job.salary_min && `$${job.salary_min.toLocaleString()}`}
                  {job.salary_min && job.salary_max && ' - '}
                  {job.salary_max && `$${job.salary_max.toLocaleString()}`}
                </div>
              )}
            </div>
            {/* Phase 1B — region context */}
            {!job.remote && (
              <RegionContextLine
                distanceMiles={job.distance_to_candidate_home_miles ?? job.distance_miles}
                city={job.location}
                remote={job.remote}
                testId="job-detail-region-context"
                className="mb-4"
              />
            )}
          </div>

          {isSkillbridge ? (
            <div data-testid="skillbridge-detail-sections">
              {sbSections.about && (
                <section className="mb-8" data-testid="sb-about">
                  <h2 className="text-xl font-semibold text-slate-900 mb-3">About this opportunity</h2>
                  <p className="text-slate-700 leading-relaxed whitespace-pre-line">{sbSections.about}</p>
                </section>
              )}

              {sbSections.respBlocks && sbSections.respBlocks.length > 0 && (
                <section className="mb-8" data-testid="sb-responsibilities">
                  <h2 className="text-xl font-semibold text-slate-900 mb-3">What you'll do</h2>
                  <div className="space-y-3 text-slate-700">
                    {sbSections.respBlocks.map((b, i) => {
                      if (b.type === 'heading') {
                        return <h3 key={i} className="text-base font-semibold text-slate-900 mt-4">{b.text}</h3>;
                      }
                      if (b.type === 'list') {
                        return (
                          <ul key={i} className="list-disc pl-6 space-y-1.5">
                            {b.items.map((it, j) => <li key={j}>{it}</li>)}
                          </ul>
                        );
                      }
                      return <p key={i} className="leading-relaxed">{b.text}</p>;
                    })}
                  </div>
                </section>
              )}

              {sbSections.eligibility && (
                <section className="mb-8" data-testid="sb-eligibility">
                  <h2 className="text-xl font-semibold text-slate-900 mb-3">Eligibility</h2>
                  <p className="text-slate-700 leading-relaxed whitespace-pre-line">{sbSections.eligibility}</p>
                </section>
              )}

              {sbSections.targetMocs && (
                <section className="mb-8" data-testid="sb-target-mocs">
                  <h2 className="text-xl font-semibold text-slate-900 mb-3">Target military occupations</h2>
                  <p className="text-slate-700 leading-relaxed">{sbSections.targetMocs}</p>
                </section>
              )}

              {sbSections.careerFields && sbSections.careerFields.length > 0 && (
                <section className="mb-8" data-testid="sb-career-fields">
                  <h2 className="text-xl font-semibold text-slate-900 mb-3">Career fields</h2>
                  <div className="flex flex-wrap gap-2">
                    {sbSections.careerFields.map((cf, i) => (
                      <span key={i} className="px-3 py-1 bg-slate-100 text-slate-700 rounded-md text-sm font-medium" data-testid="sb-career-field-pill">
                        {cf}
                      </span>
                    ))}
                  </div>
                </section>
              )}

              {sbSections.program.length > 0 && (
                <section className="mb-8" data-testid="sb-program-details">
                  <h2 className="text-xl font-semibold text-slate-900 mb-3">Program details</h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4">
                    {sbSections.program.map((row) => (
                      <div key={row.key} data-testid={`sb-program-${row.key}`}>
                        <div className="text-xs font-medium uppercase tracking-wider text-slate-400 mb-1">{row.label}</div>
                        <div className="text-slate-800">{row.value}</div>
                      </div>
                    ))}
                  </div>
                </section>
              )}

              {sbSections.notes && (
                <section className="mb-8" data-testid="sb-notes">
                  <h2 className="text-xl font-semibold text-slate-900 mb-3">Additional notes</h2>
                  <p className="text-slate-700 leading-relaxed whitespace-pre-line">{sbSections.notes}</p>
                </section>
              )}
            </div>
          ) : (
            <>
              <div className="mb-8">
                <h2 className="text-xl font-semibold text-slate-900 mb-3">About the role</h2>
                <p className="text-slate-700 whitespace-pre-wrap">{job.description}</p>
              </div>

              {job.requirements && (Array.isArray(job.requirements) ? job.requirements.length > 0 : !!job.requirements) && job.source_type !== 'external' && (
                <div className="mb-8">
                  <h2 className="text-xl font-semibold text-slate-900 mb-3">Requirements</h2>
                  <p className="text-slate-700 whitespace-pre-wrap">{Array.isArray(job.requirements) ? job.requirements.join('\n') : job.requirements}</p>
                </div>
              )}
            </>
          )}

          {/* External job: Original Posting section */}
          {job.source_type === 'external' && (
            <div className="mb-8">
              {job.requirements && (Array.isArray(job.requirements) ? job.requirements.length > 0 : !!job.requirements) && (
                <div className="mb-6">
                  <h2 className="text-xl font-semibold text-slate-900 mb-3">Requirements</h2>
                  <p className="text-slate-700 whitespace-pre-wrap">{Array.isArray(job.requirements) ? job.requirements.join('\n') : job.requirements}</p>
                </div>
              )}
              <div className="p-5 bg-slate-50 rounded-xl border border-slate-200">
                <h2 className="text-lg font-semibold text-slate-900 mb-2">Original Posting</h2>
                <p className="text-sm text-slate-500 mb-4">Source: {job.source_name || 'Lowcountry Jobs Now'}</p>
                {(job.canonical_url || job.source_url) && (
                  <a
                    href={job.canonical_url || job.source_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-5 py-2.5 bg-brand hover:bg-brand-hover text-white text-sm font-medium rounded-lg transition-colors"
                    data-testid="view-original-posting-btn"
                  >
                    View Original Posting
                    <ExternalLink className="w-4 h-4" />
                  </a>
                )}
              </div>
            </div>
          )}

          {/* CTA Section - Role-aware rendering */}
          <div className="border-t border-slate-200 pt-6">
            {/* Owner/Admin management controls remain here. All applicant-facing actions
                (SkillBridge, external, native employment) use the canonical OpportunityActions
                component — the SAME logic reused by the Opportunities map preview. */}
            {job.opportunity_type !== 'skillbridge' && job.source_type !== 'external' && canManageJob ? (
              <div data-testid="employer-controls">
                <h3 className="text-sm font-medium text-slate-500 uppercase tracking-wider mb-4">Manage This Job</h3>
                <div className="flex flex-wrap gap-3">
                  <Button
                    data-testid="edit-job-btn"
                    onClick={() => setEditDialogOpen(true)}
                    variant="outline"
                    className="border-slate-300 text-slate-700"
                  >
                    <Edit2 className="w-4 h-4 mr-2" />
                    Edit Job
                  </Button>
                  <Button
                    data-testid="toggle-status-btn"
                    onClick={() => setStatusDialogOpen(true)}
                    variant="outline"
                    className={isJobActive ? 'border-amber-300 text-amber-700 hover:bg-amber-50' : 'border-green-300 text-green-700 hover:bg-green-50'}
                  >
                    <Power className="w-4 h-4 mr-2" />
                    {isJobActive ? 'Deactivate' : 'Activate'}
                  </Button>
                  <Button
                    data-testid="delete-job-btn"
                    onClick={() => setDeleteDialogOpen(true)}
                    variant="outline"
                    className="border-red-300 text-red-700 hover:bg-red-50"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete
                  </Button>
                </div>
              </div>
            ) : (
              <OpportunityActions job={job} user={user} resume={resume} />
            )}
          </div>
        </Card>
        )}

        {/* Edit Job Dialog */}
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="edit-job-dialog">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold text-slate-900">Edit Job Posting</DialogTitle>
            </DialogHeader>

            <div className="space-y-4 mt-4">
              <div>
                <Label className="text-slate-700">Job Title *</Label>
                <Input
                  value={editForm.title}
                  onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                  className="mt-1"
                />
              </div>

              <div>
                <Label className="text-slate-700">Description *</Label>
                <Textarea
                  value={editForm.description}
                  onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                  className="mt-1 min-h-[120px]"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-700">Location</Label>
                  {savedLocations.length > 0 ? (
                    <select
                      data-testid="edit-location-select"
                      value={editForm.employer_location_id}
                      onChange={(e) => setEditForm({ ...editForm, employer_location_id: e.target.value })}
                      className="flex h-10 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm mt-1"
                    >
                      <option value="">
                        {job?.employer_location_id ? 'Select a location...' : `Current: ${job?.location || 'Not set'}`}
                      </option>
                      {savedLocations.map(loc => {
                        const parts = [loc.city, loc.state].filter(Boolean).join(', ');
                        return (
                          <option key={loc.location_id} value={loc.location_id}>
                            {parts ? `${loc.label} — ${parts}` : loc.label}
                          </option>
                        );
                      })}
                    </select>
                  ) : (
                    <p className="mt-1 text-sm text-slate-500 italic py-2">
                      {job?.location || 'No location'} — Add locations in Settings to change
                    </p>
                  )}
                </div>
                <div>
                  <Label className="text-slate-700">Job Type</Label>
                  <Select value={editForm.job_type} onValueChange={(v) => setEditForm({ ...editForm, job_type: v })}>
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="full_time">Full Time</SelectItem>
                      <SelectItem value="part_time">Part Time</SelectItem>
                      <SelectItem value="contract">Contract</SelectItem>
                      <SelectItem value="internship">Internship</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <Switch
                  checked={editForm.remote}
                  onCheckedChange={(v) => setEditForm({ ...editForm, remote: v })}
                />
                <Label className="text-slate-700">Remote position</Label>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-700">Salary Min</Label>
                  <Input
                    type="number"
                    value={editForm.salary_min}
                    onChange={(e) => setEditForm({ ...editForm, salary_min: e.target.value })}
                    className="mt-1"
                    placeholder="e.g., 50000"
                  />
                </div>
                <div>
                  <Label className="text-slate-700">Salary Max</Label>
                  <Input
                    type="number"
                    value={editForm.salary_max}
                    onChange={(e) => setEditForm({ ...editForm, salary_max: e.target.value })}
                    className="mt-1"
                    placeholder="e.g., 80000"
                  />
                </div>
              </div>

              <div>
                <Label className="text-slate-700">Requirements</Label>
                <Textarea
                  value={editForm.requirements}
                  onChange={(e) => setEditForm({ ...editForm, requirements: e.target.value })}
                  className="mt-1 min-h-[100px]"
                />
              </div>
            </div>

            <DialogFooter className="mt-6">
              <Button variant="outline" onClick={() => setEditDialogOpen(false)} className="border-slate-300">
                Cancel
              </Button>
              <Button
                onClick={handleEditJob}
                disabled={actionLoading || !editForm.title || !editForm.description}
                className="bg-brand hover:bg-brand-hover text-white"
              >
                {actionLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Save Changes
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Toggle Status Confirmation Dialog */}
        <Dialog open={statusDialogOpen} onOpenChange={setStatusDialogOpen}>
          <DialogContent data-testid="status-dialog">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold text-slate-900">
                {isJobActive ? 'Deactivate Job?' : 'Activate Job?'}
              </DialogTitle>
              <DialogDescription className="text-slate-600 pt-2">
                {isJobActive 
                  ? 'This will remove the job from public listings. Job seekers will no longer be able to see or apply to this position.'
                  : 'This will make the job visible on the public job board again. Job seekers will be able to see and apply to this position.'
                }
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="mt-6">
              <Button variant="outline" onClick={() => setStatusDialogOpen(false)} className="border-slate-300">
                Cancel
              </Button>
              <Button
                onClick={handleToggleStatus}
                disabled={actionLoading}
                className={isJobActive ? 'bg-amber-600 hover:bg-amber-700 text-white' : 'bg-green-600 hover:bg-green-700 text-white'}
              >
                {actionLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                {isJobActive ? 'Deactivate' : 'Activate'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <DialogContent data-testid="delete-dialog">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold text-red-700 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5" />
                Delete Job Posting?
              </DialogTitle>
              <DialogDescription className="text-slate-600 pt-2">
                This action cannot be undone. The job will be permanently removed and all applications will be archived.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="mt-6">
              <Button variant="outline" onClick={() => setDeleteDialogOpen(false)} className="border-slate-300">
                Cancel
              </Button>
              <Button
                onClick={handleDeleteJob}
                disabled={actionLoading}
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                {actionLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Delete Job
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}

export default JobDetails;
